import type { CategoriaGasto, FuenteGasto, ImpuestoDeGasto, UnidadInsumo } from '@tanteo/compartido';
import type { MesDeCaja } from './caja';
import { ErrorDeApi } from './error-api';
import { sumarVentas } from '@tanteo/compartido';
import { supabase } from './supabase';

export interface GastoResumen {
  id: string;
  fuente: FuenteGasto;
  cufe: string | null;
  descripcion: string | null;
  /** El proveedor con su documento, para corregir el gasto sin volver a escribirlo. */
  proveedor: { nombre: string; tipoDocumento: string; numeroDocumento: string } | null;
  /** El día de operación: cuándo salió la plata. Es el que cuadra la caja. */
  fecha: string;
  /** De dónde salió la plata: la cuenta, o `null` si salió de la caja general. */
  cuentaId: string | null;
  cuenta: string | null;
  cuentaEsEfectivo: boolean;
  cajaGeneral: boolean;
  total: number;
  subtotal: number | null;
  categoria: CategoriaGasto;
  /** El desglose por tributo, tal como se guardó. */
  impuestos: ImpuestoDeGasto[];
  /** `null` mientras el gasto cuenta; si se anuló, el motivo. */
  anuladoMotivo: string | null;
  creadoEn: string;
}

export async function listarGastos(negocioId: string): Promise<GastoResumen[]> {
  const { data, error, status } = await supabase
    .from('gastos')
    .select('id, fuente, cufe, descripcion, fecha, cuenta_id, es_caja_general, total, subtotal, categoria, anulado_motivo, creado_en, terceros:proveedor_id ( nombre, documento_tipo, documento_numero ), cuentas_negocio:cuenta_id ( cuenta_nombre, es_efectivo ), gasto_impuestos ( tipo, nombre, codigo_dian, tarifa, monto )')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('creado_en', { ascending: false })
    .limit(200);
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((g) => {
    const tercero = g.terceros as unknown as { nombre: string; documento_tipo: string; documento_numero: string } | null;
    const cuenta = g.cuentas_negocio as unknown as { cuenta_nombre: string; es_efectivo: boolean } | null;
    const impuestos = (g.gasto_impuestos ?? []) as unknown as {
      tipo: ImpuestoDeGasto['tipo'];
      nombre: string | null;
      codigo_dian: string | null;
      tarifa: number | string | null;
      monto: number | string;
    }[];
    return {
      id: g.id,
      fuente: g.fuente,
      cufe: g.cufe,
      descripcion: g.descripcion,
      proveedor: tercero
        ? { nombre: tercero.nombre, tipoDocumento: tercero.documento_tipo, numeroDocumento: tercero.documento_numero }
        : null,
      fecha: g.fecha,
      cuentaId: g.cuenta_id,
      cuenta: cuenta?.cuenta_nombre ?? null,
      cuentaEsEfectivo: cuenta?.es_efectivo ?? false,
      cajaGeneral: g.es_caja_general,
      total: Number(g.total),
      subtotal: g.subtotal === null ? null : Number(g.subtotal),
      categoria: g.categoria,
      impuestos: impuestos.map((i) => ({
        tipo: i.tipo,
        nombre: i.nombre,
        codigoDian: i.codigo_dian,
        tarifa: i.tarifa === null ? null : Number(i.tarifa),
        monto: Number(i.monto),
      })),
      anuladoMotivo: g.anulado_motivo,
      creadoEn: g.creado_en,
    };
  });
}

/** Los días cerrados desde una fecha: un gasto de esos días no se corrige ni se anula sin reabrirlo. */
export async function diasCerrados(negocioId: string, desde: string): Promise<Set<string>> {
  const { data, error, status } = await supabase
    .from('caja_cuadres')
    .select('fecha')
    .eq('negocio_id', negocioId)
    .eq('estado', 'cerrado')
    .gte('fecha', desde);
  if (error) throw new ErrorDeApi(error, status);
  return new Set((data ?? []).map((c) => c.fecha as string));
}

const impuestosParaRpc = (impuestos: ImpuestoDeGasto[]) =>
  impuestos.map((i) => ({ tipo: i.tipo, nombre: i.nombre, codigo_dian: i.codigoDian, tarifa: i.tarifa, monto: i.monto }));

export async function registrarGastoRpc(params: {
  negocioId: string;
  uuidCliente: string;
  fuente: FuenteGasto;
  total: number;
  categoria: CategoriaGasto;
  /** Qué se compró. El servidor la exige cuando el gasto es manual: no hay documento del que leerla. */
  descripcion?: string | null;
  cufe?: string | null;
  qrCrudo?: string | null;
  nitEmisor?: string | null;
  fechaDocumento?: string | null;
  /** La cuenta por la que salió la plata; excluyente con `cajaGeneral`. */
  cuentaId?: string | null;
  /** Salió de la caja general, la que el arqueo del mostrador no cuenta. */
  cajaGeneral?: boolean;
  subtotal?: number | null;
  /** El desglose por tributo. De aquí sale el `iva` que el gasto guarda como valor derivado. */
  impuestos?: ImpuestoDeGasto[];
  /** Tipo de documento del proveedor (códigos DIAN); `31` es NIT. */
  proveedorTipoDoc?: string | null;
  proveedorNombre?: string | null;
}): Promise<string> {
  const { data, error, status } = await supabase.rpc('registrar_gasto', {
    p_negocio: params.negocioId,
    p_uuid_cliente: params.uuidCliente,
    p_fuente: params.fuente,
    p_total: params.total,
    p_categoria: params.categoria,
    p_cufe: params.cufe ?? null,
    p_qr_crudo: params.qrCrudo ?? null,
    p_nit_emisor: params.nitEmisor ?? null,
    p_fecha_documento: params.fechaDocumento ?? null,
    p_subtotal: params.subtotal ?? null,
    p_impuestos: params.impuestos ? impuestosParaRpc(params.impuestos) : null,
    p_proveedor_tipo_doc: params.proveedorTipoDoc ?? null,
    p_proveedor_nombre: params.proveedorNombre ?? null,
    p_cuenta: params.cuentaId ?? null,
    p_caja_general: params.cajaGeneral ?? false,
    p_descripcion: params.descripcion ?? null,
  });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

/** Corrige un gasto de un día abierto: lo mismo que se pide al registrarlo, con el desglose completo. */
export async function editarGastoRpc(params: {
  gastoId: string;
  total: number;
  subtotal: number;
  categoria: CategoriaGasto;
  descripcion: string | null;
  cuentaId: string | null;
  cajaGeneral: boolean;
  impuestos: ImpuestoDeGasto[];
  proveedorTipoDoc: string | null;
  nitEmisor: string | null;
  proveedorNombre: string | null;
}): Promise<void> {
  const { error, status } = await supabase.rpc('editar_gasto', {
    p_gasto: params.gastoId,
    p_total: params.total,
    p_categoria: params.categoria,
    p_descripcion: params.descripcion,
    p_cuenta: params.cuentaId,
    p_caja_general: params.cajaGeneral,
    p_subtotal: params.subtotal,
    p_impuestos: impuestosParaRpc(params.impuestos),
    p_proveedor_tipo_doc: params.proveedorTipoDoc,
    p_nit_emisor: params.nitEmisor,
    p_proveedor_nombre: params.proveedorNombre,
  });
  if (error) throw new ErrorDeApi(error, status);
}

/** Anula un gasto de un día abierto: queda registrado con su motivo y deja de sumar. */
export async function anularGastoRpc(gastoId: string, motivo: string): Promise<void> {
  const { error, status } = await supabase.rpc('anular_gasto', { p_gasto: gastoId, p_motivo: motivo });
  if (error) throw new ErrorDeApi(error, status);
}

export interface Insumo {
  id: string;
  nombre: string;
  unidad: UnidadInsumo;
  existenciaActual: number;
  existenciaMinima: number | null;
}

export async function listarInsumos(negocioId: string): Promise<Insumo[]> {
  const { data, error, status } = await supabase
    .from('insumos')
    .select('id, nombre, unidad, existencia_actual, existencia_minima')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('nombre');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((i) => ({
    id: i.id,
    nombre: i.nombre,
    unidad: i.unidad,
    existenciaActual: Number(i.existencia_actual),
    existenciaMinima: i.existencia_minima === null ? null : Number(i.existencia_minima),
  }));
}

export async function crearInsumo(negocioId: string, i: { nombre: string; unidad: UnidadInsumo; existenciaActual: number; existenciaMinima: number | null }): Promise<void> {
  const { error, status } = await supabase.from('insumos').insert({
    negocio_id: negocioId,
    nombre: i.nombre.trim(),
    unidad: i.unidad,
    existencia_actual: i.existenciaActual,
    existencia_minima: i.existenciaMinima,
  });
  if (error) throw new ErrorDeApi(error, status);
}

export async function registrarMerma(negocioId: string, insumoId: string, cantidad: number): Promise<void> {
  const { error, status } = await supabase.from('insumo_movimientos').insert({
    negocio_id: negocioId,
    insumo_id: insumoId,
    tipo: 'merma',
    existencia_delta: -Math.abs(cantidad),
  });
  if (error) throw new ErrorDeApi(error, status);
}

interface LineaReceta {
  insumoId: string;
  insumoNombre: string;
  unidad: UnidadInsumo;
  cantidad: number;
}

export async function obtenerReceta(negocioId: string, productoId: string): Promise<LineaReceta[]> {
  const { data, error, status } = await supabase
    .from('producto_insumos')
    .select('insumo_id, cantidad, insumos ( nombre, unidad )')
    .eq('negocio_id', negocioId)
    .eq('producto_id', productoId);
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((r) => {
    const i = r.insumos as unknown as { nombre: string; unidad: UnidadInsumo };
    return { insumoId: r.insumo_id, insumoNombre: i.nombre, unidad: i.unidad, cantidad: Number(r.cantidad) };
  });
}

/**
 * La receta se guarda de una sola vez. Borrar y luego insertar son dos escrituras, y si la segunda
 * falla la receta ya no está: la función las hace en la misma transacción.
 */
export async function guardarReceta(negocioId: string, productoId: string, lineas: { insumoId: string; cantidad: number }[]): Promise<void> {
  const { error, status } = await supabase.rpc('guardar_receta', {
    p_negocio: negocioId,
    p_producto: productoId,
    p_lineas: lineas.map((it) => ({ insumo_id: it.insumoId, cantidad: it.cantidad })),
  });
  if (error) throw new ErrorDeApi(error, status);
}

export interface MesPyG {
  ventas: number;
  numVentas: number;
  gastosPorCategoria: { categoria: string; total: number }[];
  gastosTotal: number;
}

/** La caja general mes a mes, en orden: ingresos y gastos registrados, y el saldo acumulado. */
export async function cajaMesAMes(negocioId: string): Promise<MesDeCaja[]> {
  const { data, error, status } = await supabase
    .from('v_caja_mensual')
    .select('mes, ingresos, gastos, utilidad, saldo')
    .eq('negocio_id', negocioId)
    .order('mes');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((m) => ({
    mes: m.mes,
    ingresos: Number(m.ingresos),
    gastos: Number(m.gastos),
    utilidad: Number(m.utilidad),
    saldo: Number(m.saldo),
  }));
}

export async function pygMes(negocioId: string, anio: number, mes: number): Promise<MesPyG> {
  const desde = `${anio}-${String(mes).padStart(2, '0')}-01`;
  const hasta = mes === 12 ? `${anio + 1}-01-01` : `${anio}-${String(mes + 1).padStart(2, '0')}-01`;
  const [ventasRes, gastosRes] = await Promise.all([
    supabase
      .from('v_ventas_diarias')
      .select('total, num_ventas')
      .eq('negocio_id', negocioId)
      .gte('fecha', desde)
      .lt('fecha', hasta),
    supabase.from('v_gastos_mensuales').select('categoria, total').eq('negocio_id', negocioId).eq('mes', desde),
  ]);
  if (ventasRes.error) throw new ErrorDeApi(ventasRes.error, ventasRes.status);
  if (gastosRes.error) throw new ErrorDeApi(gastosRes.error, gastosRes.status);
  const { total: ventas, numVentas } = sumarVentas(ventasRes.data ?? []);
  const gastosPorCategoria = (gastosRes.data ?? []).map((g) => ({ categoria: g.categoria as string, total: Number(g.total) }));
  return { ventas, numVentas, gastosPorCategoria, gastosTotal: gastosPorCategoria.reduce((s, g) => s + g.total, 0) };
}
